import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { campaignsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Phone, Mail, MessageCircle, Search, Clock, Loader2, Route as RouteIcon, TrendingUp, BarChart3, Send } from 'lucide-react';
import { toast } from 'sonner';
import EmailEngagementDashboard from './EmailEngagementDashboard';
import CadenceAnalytics from './CadenceAnalytics';
import { VoiceAIAnalytics } from './settings/VoiceAIAnalytics';
import CampaignsTabs from '../components/campaigns/CampaignsTabs';

const CHANNELS = [
  { key: 'voice', label: 'Voice Calls', icon: Phone, accent: 'text-sky-600', bar: 'bg-sky-500', ring: 'border-sky-500/30' },
  { key: 'email', label: 'Email', icon: Mail, accent: 'text-violet-600', bar: 'bg-violet-500', ring: 'border-violet-500/30' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, accent: 'text-emerald-600', bar: 'bg-emerald-500', ring: 'border-emerald-500/30' },
];

const channelStyle = {
  voice: { icon: Phone, color: 'text-sky-600', dot: 'bg-sky-500' },
  email: { icon: Mail, color: 'text-violet-600', dot: 'bg-violet-500' },
  whatsapp: { icon: MessageCircle, color: 'text-emerald-600', dot: 'bg-emerald-500' },
  meeting: { icon: Clock, color: 'text-amber-600', dot: 'bg-amber-500' },
  other: { icon: RouteIcon, color: 'text-slate-500', dot: 'bg-slate-400' },
};

const TABS = [
  { value: 'overview', label: 'Overview', icon: BarChart3 },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'voice', label: 'Voice', icon: Phone },
  { value: 'cadence', label: 'Cadence', icon: Send },
];

function FunnelCard({ channel }) {
  const meta = CHANNELS.find((c) => c.key === channel.key);
  const Icon = meta.icon;
  const funnel = channel.data.funnel || [];
  const max = Math.max(1, ...funnel.map((s) => s.value));
  return (
    <div className={`rounded-xl border ${meta.ring} bg-card p-5`} data-testid={`funnel-card-${channel.key}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${meta.accent}`} />
          <h3 className="font-semibold">{meta.label}</h3>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{channel.data.campaigns} campaign{channel.data.campaigns !== 1 ? 's' : ''}</p>
          <p className="text-xs text-muted-foreground">{channel.data.active} active</p>
        </div>
      </div>
      <div className="space-y-2.5">
        {funnel.map((stage) => (
          <div key={stage.label} data-testid={`funnel-stage-${channel.key}-${stage.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">{stage.label}</span>
              <span className="font-semibold tabular-nums">{stage.value.toLocaleString()}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className={`h-full ${meta.bar} rounded-full transition-all`} style={{ width: `${(stage.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Overview tab: cross-channel funnels + best channel + per-lead journey ──
function OverviewTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadId, setLeadId] = useState('');
  const [journey, setJourney] = useState(null);
  const [journeyLoading, setJourneyLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await campaignsAPI.analyticsOverview();
      setOverview(data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const loadJourney = async () => {
    if (!leadId.trim()) { toast.error('Enter a lead ID'); return; }
    setJourneyLoading(true);
    setJourney(null);
    try {
      const { data } = await campaignsAPI.leadJourney(leadId.trim());
      setJourney(data);
      if (!(data.timeline || []).length) toast.info('No recorded touches for this lead yet');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Lead not found' : 'Failed to load journey');
    } finally {
      setJourneyLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading analytics…
        </div>
      ) : overview ? (
        <>
          {overview.best_channel && (
            <div className="rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-transparent p-5 flex items-center gap-4" data-testid="best-channel-card">
              <div className="shrink-0 h-11 w-11 rounded-full bg-amber-500/20 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-amber-500/80 font-medium">Best channel this week</p>
                <p className="text-lg font-semibold" data-testid="best-channel-name">
                  {overview.best_channel.label} — {overview.best_channel.rate}% {overview.best_channel.metric}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Focus your outreach here · based on {overview.best_channel.sent.toLocaleString()} recent sends.
                </p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="funnels-grid">
            {CHANNELS.map((c) => (
              <FunnelCard key={c.key} channel={{ key: c.key, data: overview[c.key] || { campaigns: 0, active: 0, funnel: [] } }} />
            ))}
          </div>
        </>
      ) : null}

      {/* Per-lead journey */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <RouteIcon className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Lead Journey Timeline</h3>
        </div>
        <div className="flex gap-2 max-w-xl">
          <Input
            value={leadId}
            onChange={(e) => setLeadId(e.target.value)}
            placeholder="Paste a lead ID to trace every touch"
            onKeyDown={(e) => e.key === 'Enter' && loadJourney()}
            data-testid="journey-lead-input"
          />
          <Button onClick={loadJourney} disabled={journeyLoading} data-testid="journey-load-btn">
            {journeyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1.5">Trace</span>
          </Button>
        </div>

        {journey && journey.lead && (
          <div className="mt-6" data-testid="journey-result">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
              <div>
                <p className="font-semibold" data-testid="journey-lead-name">{journey.lead.name || 'Unknown'}</p>
                <p className="text-xs text-muted-foreground">{journey.lead.company || '—'} · {journey.lead.email || journey.lead.phone || 'no contact'}</p>
              </div>
              {journey.lead.status && <Badge variant="secondary" className="ml-auto">{journey.lead.status}</Badge>}
            </div>

            {(journey.timeline || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No recorded touches yet.</p>
            ) : (
              <ol className="relative border-l border-border ml-2 space-y-5" data-testid="journey-timeline">
                {journey.timeline.map((ev, i) => {
                  const s = channelStyle[ev.channel] || channelStyle.other;
                  const EvIcon = s.icon;
                  return (
                    <li key={i} className="ml-5" data-testid="journey-event">
                      <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ${s.dot} ring-4 ring-card`} />
                      <div className="flex items-start gap-2">
                        <EvIcon className={`h-4 w-4 mt-0.5 ${s.color}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium capitalize">{(ev.type || '').replace(/_/g, ' ')}</p>
                          {ev.description && <p className="text-xs text-muted-foreground break-words">{ev.description}</p>}
                          <p className="text-[11px] text-muted-foreground mt-0.5">{ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}{ev.status ? ` · ${ev.status}` : ''}</p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── WhatsApp tab: the WhatsApp funnel from the same overview endpoint ──
function WhatsAppTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await campaignsAPI.analyticsOverview();
        setOverview(data);
      } catch { toast.error('Failed to load WhatsApp analytics'); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return (
    <div className="flex items-center justify-center py-20 text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading WhatsApp analytics…
    </div>
  );
  const wa = overview?.whatsapp || { campaigns: 0, active: 0, funnel: [] };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="whatsapp-analytics">
      <FunnelCard channel={{ key: 'whatsapp', data: wa }} />
      <div className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
        <p className="font-semibold text-foreground mb-1">About WhatsApp analytics</p>
        <p>Queued, sent, failed and skipped counts for your WhatsApp broadcasts. Replies auto-promote leads to your pipeline and appear under each lead's journey (Overview tab).</p>
      </div>
    </div>
  );
}

export default function CampaignAnalytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('tab');
  const [tab, setTab] = useState(TABS.some((t) => t.value === initial) ? initial : 'overview');

  const onTabChange = (v) => {
    setTab(v);
    setSearchParams(v === 'overview' ? {} : { tab: v }, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="campaign-analytics-page">
      <CampaignsTabs />
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="analytics-title">Campaign Analytics Hub</h1>
        <p className="text-sm text-muted-foreground mt-1">One place for every channel — Overview, Email, WhatsApp, Voice &amp; Cadence</p>
      </div>

      <Tabs value={tab} onValueChange={onTabChange} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto" data-testid="analytics-hub-tabs">
          {TABS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value} className="gap-2" data-testid={`hub-tab-${value}`}>
              <Icon className="h-4 w-4" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Each tab lazily mounts (Radix unmounts inactive content) → only the
            active tab fetches data / runs polling, and cleanup runs on switch. */}
        <TabsContent value="overview" data-testid="hub-panel-overview"><OverviewTab /></TabsContent>
        <TabsContent value="email" data-testid="hub-panel-email"><EmailEngagementDashboard /></TabsContent>
        <TabsContent value="whatsapp" data-testid="hub-panel-whatsapp"><WhatsAppTab /></TabsContent>
        <TabsContent value="voice" data-testid="hub-panel-voice"><VoiceAIAnalytics /></TabsContent>
        <TabsContent value="cadence" data-testid="hub-panel-cadence"><CadenceAnalytics /></TabsContent>
      </Tabs>
    </div>
  );
}
